import { useEffect, useState } from 'react'

// Start the next batch while the sentinel is still this far below the viewport, so cards are
// already mounted by the time the reader scrolls to them.
const SENTINEL_ROOT_MARGIN = '0px 0px 1200px 0px'

type UseIncrementalRenderOptions = {
  total: number
  // A value that changes when the reader changes what the list shows (search, filters, language),
  // not when the data behind it is refreshed; the visible count falls back to `initialCount` for
  // it. A change of `total` alone never resets: the visible count is just clamped to it.
  resetKey: string
  initialCount: number
  step: number
}

/**
 * Mounts a long list in batches: `initialCount` items first, then `step` more each time the
 * element handed to the returned `sentinelRef` (placed right after the last rendered item) comes
 * within `SENTINEL_ROOT_MARGIN` of the viewport.
 *
 * `sentinelRef` is a callback ref, so the observer attaches whenever the sentinel node mounts,
 * however late, and detaches when it unmounts.
 *
 * Without `IntersectionObserver` (very old browsers, jsdom) there is nothing to trigger the next
 * batch, so everything is rendered at once rather than stranding the rest of the list.
 */
export const useIncrementalRender = ({ total, resetKey, initialCount, step }: UseIncrementalRenderOptions) => {
  const [growth, setGrowth] = useState<{ resetKey: string; count: number }>({ resetKey, count: initialCount })
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null)
  const canObserve = typeof IntersectionObserver !== 'undefined'

  // Adjusted during render (not in an effect) so a stale count never paints. Storing the reset,
  // rather than only ignoring the stale count, also keeps a key the reader returns to (clearing
  // the search) from resurrecting the count it had before.
  if (growth.resetKey !== resetKey) setGrowth({ resetKey, count: initialCount })
  const currentCount = growth.resetKey === resetKey ? growth.count : initialCount
  const visibleCount = canObserve ? Math.min(currentCount, total) : total
  const hasMore = canObserve && visibleCount < total

  useEffect(() => {
    if (!hasMore || !sentinel) return

    // A fresh observer reports the sentinel's current state straight away, so a batch that still
    // leaves the sentinel inside the margin immediately asks for the next one.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setGrowth((previous) => ({ resetKey, count: (previous.resetKey === resetKey ? previous.count : initialCount) + step }))
        }
      },
      { rootMargin: SENTINEL_ROOT_MARGIN }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [sentinel, hasMore, visibleCount, resetKey, initialCount, step])

  return { visibleCount, hasMore, sentinelRef: setSentinel }
}
